import { createContext, useContext, useEffect, useLayoutEffect, useState } from 'react'

export type PageLayout = 'scroll' | 'fill'

export interface PageOptions {
  layout?: PageLayout
  description?: string
  /**
   * Puts the content in a centered column of this width and lines the page header and save bar up with it, e.g.
   * `CONTENT_WIDTH`. Without it the content takes the full width.
   */
  contentWidth?: number
  /** Uses the settings form theme (`formPageTheme`) for the content. */
  formTheme?: boolean
  /** Padding around the content in pixels. Defaults to the theme's `paddingLG`. */
  contentPadding?: number
}

interface PageContextValue {
  /** Element in the page header that `PageActions` render into. Null until the header has mounted. */
  actionsNode: HTMLElement | null
  /** Element below the scrolling content that `PageSaveBar` renders into. */
  footerNode: HTMLElement | null
  /** Width of the content column when the page has one, so the save bar lines up with it. */
  contentWidth?: number
  /** Element that scrolls the page content, or null when the window scrolls. */
  scrollContainer: HTMLElement | null
  /** Lets page content override the layout of a shared page frame (e.g. SettingLayout). */
  setOptions: (options: PageOptions | null) => void
  /** Tells the page whether one of its components is loading; the page shows loading while any of them is. */
  setReporterLoading: (reporter: symbol, loading: boolean) => void
}

export const PageContext = createContext<PageContextValue | null>(null)

export const usePageContext = () => useContext(PageContext)

/**
 * Sets the layout of the surrounding `Page` while the calling component is mounted. For pages rendered
 * inside a shared page frame, e.g. a settings page whose table should fill the page.
 */
export const usePageOptions = ({ layout, description, contentWidth, formTheme, contentPadding }: PageOptions) => {
  const setOptions = usePageContext()?.setOptions

  // Layout effect, so the page is first painted with these options instead of jumping to them afterwards
  useLayoutEffect(() => {
    setOptions?.({ layout, description, contentWidth, formTheme, contentPadding })
    return () => setOptions?.(null)
  }, [setOptions, layout, description, contentWidth, formTheme, contentPadding])
}

/**
 * Reports loading data to the surrounding `Page`, which shows it the same way on every page: a loading
 * indicator on the first load, and a dimmed overlay over the content on later loads. Several components of a
 * page can report; the page is loading while any of them is. Does nothing outside a `Page`.
 */
export const usePageLoading = (loading: boolean) => {
  const setReporterLoading = usePageContext()?.setReporterLoading
  const [reporter] = useState(() => Symbol('page-loading'))

  useEffect(() => {
    setReporterLoading?.(reporter, loading)
  }, [setReporterLoading, reporter, loading])

  useEffect(() => () => setReporterLoading?.(reporter, false), [setReporterLoading, reporter])
}

/** Scroll target for components that follow scrolling (e.g. `Affix`); undefined means the window. */
export const useScrollContainer = () => usePageContext()?.scrollContainer ?? undefined
